import React, { useEffect, useRef, useState } from 'react'
import { Card, Button, Typography, Space, App, Divider, Popover } from 'antd'
import { Plus, Trash2, Image as ImageIcon, ArrowRight, X } from 'lucide-react'
import { FFEDocument, ProductLibraryItem } from '../../../types/ffe'
import { formatDate } from '../../../utils/date'
import { TooltipRef } from 'antd/es/tooltip'

const { Text } = Typography

export type ProductLibrarySchedule = Pick<
  FFEDocument,
  '_id' | 'projectTitle' | 'updatedAt' | 'groups'
>

interface Props {
  product: ProductLibraryItem
  onAddToSchedule: (
    productId: string,
    scheduleId?: string,
    groupId?: string
  ) => void
  onDelete: (productId: string) => void
  selectedScheduleId: string | undefined | null
  setSelectedScheduleId: (scheduleId: string | undefined | null) => void
  schedules: Array<ProductLibrarySchedule>
  selectedGroupId: string | undefined | null
  setSelectedGroupId: (groupId: string | undefined | null) => void
  loadingAddToSchedule: boolean
}

export const ProductCard: React.FC<Props> = ({
  product,
  onAddToSchedule,
  onDelete,
  selectedScheduleId,
  setSelectedScheduleId,
  selectedGroupId,
  setSelectedGroupId,
  loadingAddToSchedule,
  schedules
}) => {
  const { modal } = App.useApp()
  const [popoverOpen, setPopoverOpen] = useState(false)

  const handleDelete = () => {
    modal.confirm({
      title: 'Delete Product',
      content:
        'Are you sure you want to remove this product from your library?',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => onDelete(product._id)
    })
  }

  const image = product.lineItem
    ? product.lineItem.image
    : product.material?.image

  const selectedSchedule = schedules.find(
    (schedule) => schedule._id === selectedScheduleId
  )
  const selectedGroup = selectedSchedule?.groups.find(
    (group) => group.id === selectedGroupId
  )

  useEffect(() => {
    if (!loadingAddToSchedule) {
      setPopoverOpen(false)
    }
  }, [loadingAddToSchedule])

  return (
    <Card
      className="group transition-shadow hover:shadow-lg"
      styles={{ body: { padding: 0 } }}
    >
      {/* Image or Placeholder */}
      <div className="aspect-square bg-gray-50">
        {image ? (
          <img
            alt={image.alt}
            src={image.url}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
            <ImageIcon className="h-12 w-12 mb-2" />
            <Text type="secondary">No Image</Text>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <Space direction="vertical" className="w-full">
          <div>
            <Text strong className="block text-lg">
              {product.supplierItemCode}
            </Text>
            <Text type="secondary" className="block">
              {product.supplierName}
            </Text>
          </div>

          <div className="flex gap-2">
            <Text type="secondary" className="capitalize">
              {product.name}
            </Text>
          </div>
        </Space>
      </div>

      {/* Actions */}
      <Divider style={{ margin: 0 }} />
      <div className="px-4 py-3 flex items-center justify-between">
        <Popover
          trigger={['click']}
          placement="bottom"
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          content={
            <>
              {selectedScheduleId === null ? (
                <Space
                  className="min-w-[300px]"
                  direction="vertical"
                  size="small"
                >
                  <Text className="text-xs">Select Project</Text>
                  {schedules.map((schedule) => (
                    <Button
                      key={schedule._id}
                      className="w-full flex"
                      type="text"
                      onClick={() => setSelectedScheduleId(schedule._id)}
                      iconPosition="end"
                      icon={<ArrowRight className="h-4 w-4" />}
                    >
                      <Text type="secondary" className="block flex-1">
                        {formatDate(schedule.updatedAt ?? '')}
                      </Text>
                      <Text strong className="block">
                        {schedule.projectTitle || 'Untitled Project'}
                      </Text>
                    </Button>
                  ))}
                </Space>
              ) : (
                <Space
                  className="min-w-[300px]"
                  direction="vertical"
                  size="small"
                >
                  <Text className="text-xs">Project</Text>
                  <Button
                    type="text"
                    className="flex w-full"
                    icon={<X className="h-4 w-4" />}
                    iconPosition="end"
                    onClick={() => {
                      setSelectedGroupId(null)
                      setSelectedScheduleId(null)
                    }}
                  >
                    <Text className="w-full">
                      {selectedSchedule?.projectTitle || 'Untitled Project'}
                    </Text>
                  </Button>
                  <Divider style={{ margin: 0 }} />
                  {selectedGroupId === null ? (
                    <>
                      <Text className="text-xs">Select Group</Text>
                      <div className="space-y-1 overflow-y-auto max-h-40">
                        {selectedSchedule?.groups.map((group) => (
                          <Button
                            key={group.id}
                            className="w-full flex"
                            type="text"
                            onClick={() => setSelectedGroupId(group.id)}
                            iconPosition="end"
                            icon={<ArrowRight className="h-4 w-4" />}
                          >
                            <Text strong className="block flex-1">
                              {group.name || 'Untitled Group'}
                            </Text>
                          </Button>
                        ))}
                        <Button
                          className="w-full flex"
                          type="text"
                          onClick={() => setSelectedGroupId(undefined)}
                          iconPosition="end"
                          icon={<ArrowRight className="h-4 w-4" />}
                        >
                          <Text strong className="block flex-1">
                            {'No Group'}
                          </Text>
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Text className="text-xs">Group</Text>
                      <Button
                        type="text"
                        className="flex w-full"
                        icon={<X className="h-4 w-4" />}
                        iconPosition="end"
                        onClick={() => setSelectedGroupId(null)}
                      >
                        <Text className="w-full">
                          {selectedGroup?.name || 'Untitled Group'}
                        </Text>
                      </Button>
                      <Divider style={{ margin: 0 }} />
                      <Button
                        onClick={() =>
                          onAddToSchedule(
                            product._id,
                            selectedScheduleId,
                            selectedGroupId
                          )
                        }
                        type="primary"
                        className="w-full"
                        size="large"
                        icon={<Plus className="h-4 w-4" />}
                        loading={loadingAddToSchedule}
                      >
                        Add to Schedule
                      </Button>
                    </>
                  )}
                </Space>
              )}
            </>
          }
        >
          <Button type="text" icon={<Plus className="h-4 w-4" />}>
            Add to Schedule
          </Button>
        </Popover>

        <Button
          type="text"
          danger
          icon={<Trash2 className="h-4 w-4" />}
          onClick={handleDelete}
        />
      </div>
    </Card>
  )
}
